import { Badge, Button, Card, Select, Table, Tag } from "antd";
import { useGetPartnerInviteInfoQuery } from "api/Partner/hooks";
import tenantAxiosInstance from "api/tenantAxiosInstance";
import { useDestroyRequestMutation, useStoreRequestMutation } from "api/TenantSlug/Request/hooks";
import { Drawer } from "components/UI/Drawer";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle";
import { useDrawer } from "hooks/useDrawer";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { RoleEnum } from "types/enums";
import { DrawerId } from "types/models";
import { getDrawerSearchParams } from "utils/drawerHelpers";
import { getTenantRoleBadgeColor, getTenantRoleGroups } from "utils/getTenantRoleBadge";

import PartnerInviteInfoTenantData = App.Data.PartnerInviteInfoTenantData;

interface InvitePartnerDrawerProps {
    partnerId: string;
}

const runWithTenantSlug = async <T,>(slugName: string, fn: () => Promise<T>): Promise<T> => {
    const previousBaseUrl = tenantAxiosInstance.defaults.baseURL;
    tenantAxiosInstance.setTenantSlugInUrl(slugName);
    try {
        return await fn();
    } finally {
        tenantAxiosInstance.defaults.baseURL = previousBaseUrl;
    }
};

export const InvitePartnerDrawer: FC<InvitePartnerDrawerProps> = ({ partnerId }) => {
    const { t } = usePaldockTranslation("drawers.invite-partner");
    const { t: tCommon } = usePaldockTranslation("common");
    const id = DrawerId.InvitePartner;
    const { hide } = useDrawer();

    const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
    const [cancellingTenantId, setCancellingTenantId] = useState<string | null>(null);

    const searchParamsToClean = getDrawerSearchParams(id, "partnerId");

    const inviteInfoQuery = useGetPartnerInviteInfoQuery(partnerId);
    const tenants = useMemo(() => inviteInfoQuery.data?.tenants ?? [], [inviteInfoQuery.data?.tenants]);

    const tenantOptions = useMemo(
        () => tenants.filter((tenant) => tenant.invitable).map((tenant) => ({ label: tenant.title, value: tenant.id })),
        [tenants]
    );

    const storeRequestMutation = useStoreRequestMutation();
    const destroyRequestMutation = useDestroyRequestMutation();

    const handleSubmit = async () => {
        const selectedTenant = tenants.find((tenant) => tenant.id === selectedTenantId);
        if (!selectedTenant) {
            return;
        }

        try {
            await runWithTenantSlug(selectedTenant.slug_name, () =>
                storeRequestMutation.mutateAsync({
                    user_id: partnerId,
                    type: "tenant-invite",
                    tenant_id: selectedTenant.id,
                    data: { roles: ["AffiliatePartner"] },
                    return_url: `${window.location.origin}/${selectedTenant.slug_name}/auth/accept-invitation/{id}/{hash}`,
                })
            );

            toast.success(t("messages.success"));
            hide(id, searchParamsToClean);
        } catch {
            toast.error(t("messages.error"));
        }
    };

    const handleCancelInvite = async (tenant: PartnerInviteInfoTenantData) => {
        if (!tenant.pending_user_request_id) {
            return;
        }

        setCancellingTenantId(tenant.id);
        try {
            await runWithTenantSlug(tenant.slug_name, () =>
                destroyRequestMutation.mutateAsync(tenant.pending_user_request_id as string)
            );

            toast.success(t("messages.cancelled"));
            inviteInfoQuery.refetch();
        } catch {
            toast.error(t("messages.cancel_error"));
        } finally {
            setCancellingTenantId(null);
        }
    };

    return (
        <Drawer
            drawerId={id}
            title={<DrawerTitle mainTitle={t("title")} />}
            searchParamsToClean={searchParamsToClean}
            loading={inviteInfoQuery.isLoading}
            extraButtons={
                <Button
                    size="large"
                    type="primary"
                    loading={storeRequestMutation.isPending}
                    disabled={!selectedTenantId}
                    onClick={handleSubmit}
                >
                    {t("submit")}
                </Button>
            }
        >
            <DrawerContent>
                <Card className="rounded-md m-4">
                    <div className="mb-1 font-medium">{t("select_tenant")}</div>
                    <Select
                        size="large"
                        showSearch
                        className="w-full"
                        placeholder={t("select_tenant_placeholder")}
                        value={selectedTenantId}
                        onChange={setSelectedTenantId}
                        options={tenantOptions}
                        filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </Card>
                <Card className="rounded-md m-4">
                    <div className="mb-2 font-medium">{t("roles_table.title")}</div>
                    <Table<PartnerInviteInfoTenantData>
                        rowKey={(record) => record.id}
                        dataSource={tenants}
                        pagination={false}
                        columns={[
                            {
                                title: t("roles_table.columns.workspace"),
                                dataIndex: "title",
                            },
                            {
                                title: t("roles_table.columns.roles"),
                                key: "partner_roles",
                                render: (_, record) => {
                                    const roleGroups = getTenantRoleGroups(record.partner_roles as RoleEnum[]);

                                    return record.pending_user_request_id ? (
                                        <Tag color="gold">{t("roles_table.pending")}</Tag>
                                    ) : roleGroups.length > 0 ? (
                                        <div className="flex items-center gap-1 flex-wrap">
                                            {roleGroups.map((group) => (
                                                <Badge
                                                    key={group}
                                                    count={tCommon(`roles.${group}`)}
                                                    color={getTenantRoleBadgeColor(group)}
                                                    classNames={{ indicator: "!rounded !shadow-none" }}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-gray-400">{t("roles_table.no_roles")}</span>
                                    );
                                },
                            },
                            {
                                title: t("roles_table.columns.actions"),
                                key: "actions",
                                render: (_, record) =>
                                    record.pending_user_request_id ? (
                                        <Button
                                            danger
                                            size="small"
                                            loading={
                                                destroyRequestMutation.isPending && cancellingTenantId === record.id
                                            }
                                            onClick={() => handleCancelInvite(record)}
                                        >
                                            {t("roles_table.cancel")}
                                        </Button>
                                    ) : null,
                            },
                        ]}
                    />
                </Card>
            </DrawerContent>
        </Drawer>
    );
};
